/**
 * Oppsett av en sang på A4, uten jsPDF og uten document.
 * Rene funksjoner, slik at forhåndsvisning og PDF kan bruke samme oppsett.
 */

import type { ParsedLine } from '../leadsheetParser';

export interface OppsettValg {
  fontSize: number; // punkt
  chordFontSize: number; // punkt
  columns: 1 | 2;
}

export type ElementType = 'seksjon' | 'tekst' | 'akkord';

export interface Plassert {
  type: ElementType;
  tekst: string;
  x: number; // mm fra venstre kant av siden
  y: number; // mm fra toppen, grunnlinjen for teksten
  storrelse: number; // punkt
}

export interface OppsettSide {
  elementer: Plassert[];
}

export interface SangOppsett {
  sider: OppsettSide[];
  fontSize: number; // etter eventuell krymping
  chordFontSize: number;
}

export const SIDE = {
  bredde: 210,
  hoyde: 297,
  margVenstre: 15,
  margHoyre: 15,
  toppForste: 45,
  toppAndre: 20,
  bunn: 277,
  kolonneAvstand: 10,
} as const;

const COURIER_EM = 0.6;
export const MM_PER_PUNKT = 25.4 / 72;

/** Bredde i mm. Courier er 0,6 em bred, og tegn telles som kodepunkter. */
export function tekstBredde(tekst: string, storrelse: number): number {
  return Array.from(tekst).length * COURIER_EM * storrelse * MM_PER_PUNKT;
}

const MINSTE_KRYMPING = 11;
const KRYMPEFAKTOR = 0.9;
const AKKORD_AVSTAND = 2; // mm mellom to akkorder på samme rad
const SEKSJON_MARGIN = 0.25; // andel av halvdelen av høyden, ved skille på siste side
const LIK_HOYDE = 1e-6; // mm, høydeforskjeller under dette regnes som like

// Courier rekker 0,63 em over grunnlinjen og 0,16 em under.
const OVER_GRUNNLINJE_EM = 0.63;
const UNDER_GRUNNLINJE_EM = 0.16;
// Luft mellom akkord og tekst, i em av akkorden.
// Satt så standard tekst 12 / akkord 14 gir 3,92 mm, som før.
const AKKORD_LUFT_EM =
  (3.92 / MM_PER_PUNKT - 12 * OVER_GRUNNLINJE_EM - 14 * UNDER_GRUNNLINJE_EM) / 14;

/** Bredden i mm av et gitt antall tegn. */
const tegnBredde = (antall: number, storrelse: number): number =>
  antall * tekstBredde(' ', storrelse);

/** Avstanden i mm fra en tekstlinjes grunnlinje til neste, uten akkorder. */
const linjeAvstand = (fontSize: number): number => fontSize * 0.45;

/**
 * Akkordraden over en tekstlinje, i mm, regnet fra både tekst- og akkordstørrelsen.
 * hoyde: fra tekstens grunnlinje til akkordens. Tekstens høyde over grunnlinjen,
 * akkordens dybde under, og luft.
 * plass: det som settes av til akkordraden over linjen. Linjeavstanden rommer vanligvis
 * akkordens høyde over grunnlinjen, så plass er hoyde. Er akkorden stor mot teksten,
 * legges resten til, så akkorden heller ikke når opp i linjen over.
 */
function akkordMaal(fontSize: number, chordFontSize: number): { hoyde: number; plass: number } {
  const luft = AKKORD_LUFT_EM * chordFontSize;
  const hoyde =
    (OVER_GRUNNLINJE_EM * fontSize + UNDER_GRUNNLINJE_EM * chordFontSize + luft) * MM_PER_PUNKT;
  // Fra linjen over til akkordens grunnlinje trengs tekstens dybde, akkordens høyde og luft.
  const overAkkorden =
    (UNDER_GRUNNLINJE_EM * fontSize + OVER_GRUNNLINJE_EM * chordFontSize + luft) * MM_PER_PUNKT;
  return { hoyde, plass: hoyde + Math.max(0, overAkkorden - linjeAvstand(fontSize)) };
}

/** Bare to kolonner krymper. Akkordstørrelsen vokser aldri, selv om den er under grensen. */
function krympVedBehov(
  linjer: ParsedLine[],
  valg: OppsettValg,
  kolonneBredde: number
): { fontSize: number; chordFontSize: number } {
  let { fontSize, chordFontSize } = valg;
  if (valg.columns !== 2) return { fontSize, chordFontSize };

  const lengst = linjer.reduce((maks, l) => Math.max(maks, Array.from(l.text).length), 0);
  while (tegnBredde(lengst, fontSize) > kolonneBredde && fontSize > MINSTE_KRYMPING) {
    fontSize = Math.max(MINSTE_KRYMPING, fontSize * KRYMPEFAKTOR);
    chordFontSize = Math.max(
      Math.min(MINSTE_KRYMPING, chordFontSize),
      chordFontSize * KRYMPEFAKTOR
    );
  }
  return { fontSize, chordFontSize };
}

interface Del {
  tekst: string;
  akkorder: ParsedLine['chords'];
}

/** Sammenhengende tegn uten mellomrom, som [start, slutt) i tegnlisten. */
function finnOrd(tegn: string[]): { start: number; slutt: number }[] {
  const ord: { start: number; slutt: number }[] = [];
  tegn.forEach((t, i) => {
    if (t.trim() === '') return;
    const siste = ord[ord.length - 1];
    if (siste && siste.slutt === i) siste.slutt = i + 1;
    else ord.push({ start: i, slutt: i + 1 });
  });
  return ord;
}

/**
 * Bryter teksten ved mellomrom. Mellomrommene der linjen brytes, faller bort.
 * Akkorder følger tegnet sitt. Står en akkord på et slikt mellomrom, går den til neste del,
 * og står den etter tekstens slutt, går den til siste del, så ingen akkord forsvinner.
 */
function brytLinje(linje: ParsedLine, kolonneBredde: number, storrelse: number): Del[] {
  const tegn = Array.from(linje.text);
  if (tegnBredde(tegn.length, storrelse) <= kolonneBredde) {
    return [{ tekst: linje.text, akkorder: linje.chords }];
  }

  const passer = (antall: number) => tegnBredde(antall, storrelse) <= kolonneBredde;
  const omraader: { start: number; slutt: number }[] = [];
  finnOrd(tegn).forEach((ord) => {
    const aapent = omraader[omraader.length - 1];
    if (aapent && passer(ord.slutt - aapent.start)) {
      aapent.slutt = ord.slutt;
    } else if (!aapent && passer(ord.slutt)) {
      // Første del beholder innrykket, så lenge ordet likevel får plass.
      omraader.push({ start: 0, slutt: ord.slutt });
    } else {
      omraader.push({ ...ord });
    }
  });
  // En linje med bare mellomrom har ingen ord, men kan ha akkorder.
  if (omraader.length === 0) return [{ tekst: '', akkorder: linje.chords }];

  const deler: Del[] = omraader.map((o) => ({
    tekst: tegn.slice(o.start, o.slutt).join(''),
    akkorder: [],
  }));
  linje.chords.forEach((akkord) => {
    const treff = omraader.findIndex((o) => akkord.position < o.slutt);
    const indeks = treff === -1 ? omraader.length - 1 : treff;
    deler[indeks].akkorder.push({
      chord: akkord.chord,
      position: Math.max(0, akkord.position - omraader[indeks].start),
    });
  });
  return deler;
}

interface AkkordPlass {
  tekst: string;
  dx: number; // mm fra kolonnens venstre kant
  rad: number;
}

/**
 * Ingen akkorder overlapper. Det som går utenfor kolonnen, fortsetter på ny rad.
 * Raden starter med akkorden som ikke fikk plass, og de neste beholder avstanden til den.
 */
function plasserAkkorder(
  akkorder: ParsedLine['chords'],
  fontSize: number,
  chordFontSize: number,
  kolonneBredde: number
): AkkordPlass[] {
  let rad = 0;
  let radStart = 0;
  let forrigeSlutt: number | null = null;
  return akkorder.map((akkord) => {
    const bredde = tekstBredde(akkord.chord, chordFontSize);
    const naturligDx = tekstBredde(' '.repeat(akkord.position), fontSize);
    let dx = naturligDx - radStart;
    if (forrigeSlutt !== null && dx < forrigeSlutt + AKKORD_AVSTAND) {
      dx = forrigeSlutt + AKKORD_AVSTAND;
    }
    if (dx + bredde > kolonneBredde) {
      if (forrigeSlutt !== null) rad += 1;
      radStart = naturligDx;
      dx = 0;
    }
    forrigeSlutt = dx + bredde;
    return { tekst: akkord.chord, dx, rad };
  });
}

/** Én rad i kolonnen etter bryting. */
interface Rad {
  type: 'seksjon' | 'tekst';
  tekst: string;
  storrelse: number;
  luftFor: number; // mm, gjelder ikke øverst i en kolonne
  akkorder: AkkordPlass[];
  antallAkkordRader: number;
}

const erTom = (rad: Rad): boolean => rad.tekst.trim() === '' && rad.akkorder.length === 0;

function lagRader(
  linjer: ParsedLine[],
  fontSize: number,
  chordFontSize: number,
  kolonneBredde: number,
  akkordPlass: number
): Rad[] {
  const rader: Rad[] = linjer.flatMap((linje) => {
    const storrelse = linje.isSection ? fontSize + 2 : fontSize;
    return brytLinje(linje, kolonneBredde, storrelse).map((del, delIndeks) => {
      const akkorder = plasserAkkorder(del.akkorder, fontSize, chordFontSize, kolonneBredde);
      const sisteAkkord = akkorder[akkorder.length - 1];
      return {
        type: linje.isSection ? ('seksjon' as const) : ('tekst' as const),
        tekst: del.tekst,
        storrelse,
        // Luften står foran overskriften, ikke mellom delene av den.
        luftFor: linje.isSection && delIndeks === 0 ? fontSize * 0.65 : 0,
        akkorder,
        antallAkkordRader: sisteAkkord ? sisteAkkord.rad + 1 : 0,
      };
    });
  });

  // Akkordraden skal ligge under linjen over.
  return rader.map((rad, indeks) => {
    if (rad.akkorder.length === 0) return rad;
    let over = indeks - 1;
    while (over >= 0 && erTom(rader[over])) over -= 1;
    const forrige = rader[over];
    if (!forrige) return rad;
    const tomme = indeks - 1 - over;
    // En akkordlinje, og en seksjon rett over, har alt gitt plass til akkordraden.
    if (forrige.akkorder.length > 0 || (forrige.type === 'seksjon' && tomme === 0)) return rad;
    // Hver tom rad imellom gir en linjeavstand, så bare resten legges til.
    const luft = Math.max(0, akkordPlass - tomme * linjeAvstand(fontSize));
    return { ...rad, luftFor: rad.luftFor + luft };
  });
}

export function lagOppsett(linjer: ParsedLine[], valg: OppsettValg): SangOppsett {
  const innhold = SIDE.bredde - SIDE.margVenstre - SIDE.margHoyre;
  const kolonneBredde = valg.columns === 2 ? (innhold - SIDE.kolonneAvstand) / 2 : innhold;
  const { fontSize, chordFontSize } = krympVedBehov(linjer, valg, kolonneBredde);
  // Regnes etter krymping. Akkordens y bruker hoyde, og alt som setter av plass til
  // akkordraden bruker plass.
  const { hoyde: akkordHoyde, plass: akkordPlass } = akkordMaal(fontSize, chordFontSize);
  const akkordRadHoyde = chordFontSize * MM_PER_PUNKT;
  const rader = lagRader(linjer, fontSize, chordFontSize, kolonneBredde, akkordPlass);

  const grunnlinje = (rad: Rad, y: number, overst: boolean): number =>
    y + (overst ? 0 : rad.luftFor) + Math.max(0, rad.antallAkkordRader - 1) * akkordRadHoyde;

  const nesteY = (indeks: number, linjeY: number): number => {
    const rad = rader[indeks];
    if (rad.type === 'seksjon') {
      const nesteHarAkkorder = (rader[indeks + 1]?.akkorder.length ?? 0) > 0;
      return linjeY + fontSize * 0.5 + (nesteHarAkkorder ? akkordPlass : 0);
    }
    return linjeY + linjeAvstand(fontSize) + (rad.akkorder.length > 0 ? akkordPlass : 0);
  };

  /** Seksjonen, og det som følger fram til første rad med tekst eller akkorder, må få plass. */
  const seksjonFaarPlass = (start: number, startY: number, startOverst: boolean): boolean => {
    let y = startY;
    let overst = startOverst;
    for (let i = start; i < rader.length; i += 1) {
      const rad = rader[i];
      const linjeY = grunnlinje(rad, y, overst);
      if (!erTom(rad) && linjeY > SIDE.bunn) return false;
      if (rad.type === 'tekst' && !erTom(rad)) return true;
      y = nesteY(i, linjeY);
      overst = false;
    }
    return true;
  };

  /** Går gjennom radene i én kolonne. Tomme rader flytter bare y. */
  const besokKolonne = (
    fra: number,
    til: number,
    topp: number,
    besok: (rad: Rad, indeks: number, linjeY: number, startY: number) => void
  ): void => {
    let y = topp;
    for (let i = fra; i < til; i += 1) {
      const rad = rader[i];
      if (erTom(rad)) {
        y = nesteY(i, y);
      } else {
        const linjeY = grunnlinje(rad, y, y === topp);
        besok(rad, i, linjeY, y);
        y = nesteY(i, linjeY);
      }
    }
  };

  /** Avstanden fra toppen til grunnlinjen for siste rad med innhold. */
  const kolonneHoyde = (fra: number, til: number, topp: number): number => {
    let nederst = topp;
    besokKolonne(fra, til, topp, (_rad, _indeks, linjeY) => {
      nederst = linjeY;
    });
    return nederst - topp;
  };

  /**
   * Finner raden høyre kolonne skal starte på. En seksjon nær midten går foran,
   * ellers brukes raden som gir jevnest høyde. Gir null når ingen deling er gyldig.
   */
  const balansertSkille = (start: number, topp: number): number | null => {
    const slutt = rader.length;
    const halvdel = kolonneHoyde(start, slutt, topp) / 2;
    let forrigeType: Rad['type'] | null = null;
    let seksjon: { indeks: number; avstand: number } | null = null;
    let linje: { indeks: number; forskjell: number } | null = null;

    besokKolonne(start, slutt, topp, (rad, indeks, _linjeY, startY) => {
      // En seksjonsoverskrift står aldri nederst i venstre kolonne.
      const kanDele = forrigeType === 'tekst';
      forrigeType = rad.type;
      if (!kanDele) return;

      const venstre = kolonneHoyde(start, indeks, topp);
      const hoyre = kolonneHoyde(indeks, slutt, topp);
      if (topp + venstre > SIDE.bunn || topp + hoyre > SIDE.bunn) return;

      const avstand = Math.abs(startY - topp - halvdel);
      if (
        rad.type === 'seksjon' &&
        avstand <= halvdel * SEKSJON_MARGIN &&
        (seksjon === null || avstand < seksjon.avstand)
      ) {
        seksjon = { indeks, avstand };
      }
      // Ved lik forskjell får venstre kolonne den ekstra raden.
      const forskjell = Math.abs(venstre - hoyre);
      if (linje === null || forskjell <= linje.forskjell + LIK_HOYDE) {
        linje = { indeks, forskjell };
      }
    });

    // TypeScript ser ikke tilordningene i tilbakekallet over.
    const valgt = (seksjon ?? linje) as { indeks: number } | null;
    return valgt ? valgt.indeks : null;
  };

  const perSide = valg.columns;
  const toppFor = (kolonne: number): number =>
    kolonne < perSide ? SIDE.toppForste : SIDE.toppAndre;

  // Fyller kolonnene ovenfra, og noterer raden hver kolonne starter på.
  const kolonneStarter: number[] = [0];
  let topp: number = toppFor(0);
  let y = topp;

  rader.forEach((rad, indeks) => {
    // Tomme rader kan ikke gi ny kolonne eller side.
    if (erTom(rad)) {
      y = nesteY(indeks, y);
      return;
    }

    const faarPlass =
      rad.type === 'seksjon'
        ? seksjonFaarPlass(indeks, y, y === topp)
        : grunnlinje(rad, y, y === topp) <= SIDE.bunn;
    // Øverst i kolonnen plasseres raden uansett, ellers ville kolonnen stått tom.
    if (!faarPlass && y !== topp) {
      kolonneStarter.push(indeks);
      topp = toppFor(kolonneStarter.length - 1);
      y = topp;
    }
    y = nesteY(indeks, grunnlinje(rad, y, y === topp));
  });

  // Siste side deles på nytt, så kolonnene blir like høye.
  if (valg.columns === 2) {
    const sisteKolonne = kolonneStarter.length - 1;
    const venstreKolonne = sisteKolonne - (sisteKolonne % 2);
    const skille = balansertSkille(kolonneStarter[venstreKolonne], toppFor(venstreKolonne));
    if (skille !== null) {
      kolonneStarter.length = venstreKolonne + 1;
      kolonneStarter.push(skille);
    }
  }

  const sider: OppsettSide[] = [];
  kolonneStarter.forEach((start, kolonne) => {
    const sideIndeks = Math.floor(kolonne / perSide);
    if (!sider[sideIndeks]) sider[sideIndeks] = { elementer: [] };
    const elementer = sider[sideIndeks].elementer;
    const x = SIDE.margVenstre + (kolonne % perSide) * (kolonneBredde + SIDE.kolonneAvstand);
    const til = kolonne + 1 < kolonneStarter.length ? kolonneStarter[kolonne + 1] : rader.length;

    besokKolonne(start, til, toppFor(kolonne), (rad, _indeks, linjeY) => {
      const forsteAkkordRad = linjeY - (rad.antallAkkordRader - 1) * akkordRadHoyde - akkordHoyde;
      rad.akkorder.forEach((akkord) => {
        elementer.push({
          type: 'akkord',
          tekst: akkord.tekst,
          x: x + akkord.dx,
          y: forsteAkkordRad + akkord.rad * akkordRadHoyde,
          storrelse: chordFontSize,
        });
      });
      if (rad.tekst.trim() !== '') {
        elementer.push({ type: rad.type, tekst: rad.tekst, x, y: linjeY, storrelse: rad.storrelse });
      }
    });
  });

  return { sider, fontSize, chordFontSize };
}
